import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
  Tabs,
  Tab,
  Box,
  Alert,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format, isAfter } from 'date-fns';
import {
  fetchTournaments,
  fetchMyTournaments,
  Tournament,
} from '../../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tournament-tabpanel-${index}`}
      aria-labelledby={`tournament-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TournamentDashboard = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  // Fetch all tournaments (filtered by eligibility)
  const { data: allTournaments, isLoading: isLoadingAll } = useQuery(
    ['tournaments'],
    fetchTournaments
  );

  // Fetch tournaments user is registered for
  const { data: myTournaments, isLoading: isLoadingMy } = useQuery(
    ['tournaments', 'my'],
    fetchMyTournaments,
    { retry: false } // Don't retry if user is not authenticated
  );

  const isLoading = isLoadingAll || isLoadingMy;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ongoing':
        return 'success';
      case 'upcoming':
        return 'info';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Categorize tournaments
  const now = new Date();
  
  // Upcoming tournaments: registration starts in the future
  const upcomingTournaments = (allTournaments || []).filter((tournament) => {
    if (!tournament.registration_start_date) return false;
    const regStart = new Date(tournament.registration_start_date);
    return isAfter(regStart, now);
  });

  // Open for registration: tournaments where registration is currently open
  // Use allTournaments and filter by registration status, not just availableTournaments
  // (availableTournaments only shows tournaments user can register for, but we want to show all open tournaments)
  const openForRegistration = (allTournaments || []).filter((tournament) => {
    if (!tournament.registration_start_date || !tournament.registration_end_date) return false;
    const regStart = new Date(tournament.registration_start_date);
    const regEnd = new Date(tournament.registration_end_date);
    const now = new Date();
    return (now >= regStart && now < regEnd) && tournament.status !== 'completed' && tournament.status !== 'cancelled';
  });

  // My active tournaments: tournaments user is registered for and are not completed
  const myActiveTournaments = (myTournaments || []).filter(
    (tournament) => tournament.status !== 'completed' && tournament.status !== 'cancelled'
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const renderTournamentList = (tournaments: Tournament[]) => {
    if (tournaments.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No tournaments in this category
        </Alert>
      );
    }

    return (
      <List>
        {tournaments.map((tournament) => (
          <ListItem
            key={tournament.id}
            divider
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            onClick={() => navigate(`/tournaments/${tournament.id}`)}
          >
            <ListItemText
              primary={tournament.name}
              secondary={
                <>
                  {tournament.location}
                  {tournament.start_date && (
                    <> • {format(new Date(tournament.start_date), 'MMM dd, yyyy')}</>
                  )}
                  {tournament.registration_start_date &&
                    tournament.registration_end_date && (
                      <>
                        {' • Registration: '}
                        {format(new Date(tournament.registration_start_date), 'MMM dd')} -{' '}
                        {format(new Date(tournament.registration_end_date), 'MMM dd')}
                      </>
                    )}
                </>
              }
            />
            <Chip
              label={tournament.status}
              color={getStatusColor(tournament.status) as any}
              size="small"
              sx={{ ml: 1 }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading tournaments...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Tournament Dashboard" />
      <CardContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="tournament categories">
            <Tab label={`Open for Registration (${openForRegistration.length})`} />
            <Tab label={`Upcoming (${upcomingTournaments.length})`} />
            <Tab label={`My Tournaments (${myActiveTournaments.length})`} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Open for Registration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tournaments you can register for right now
          </Typography>
          {renderTournamentList(openForRegistration)}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Upcoming Tournaments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tournaments with registration opening soon
          </Typography>
          {renderTournamentList(upcomingTournaments)}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            My Active Tournaments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tournaments you are registered for
          </Typography>
          {renderTournamentList(myActiveTournaments)}
        </TabPanel>
      </CardContent>
    </Card>
  );
};

export default TournamentDashboard;

